import PageHeader from '../../components/PageHeader';
// src/pages/company/InvoicesPage.tsx
import React from 'react';
import {
  Box, Typography, Paper, Stack, Button, Alert, Skeleton,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Tooltip, IconButton, useMediaQuery, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Chip,
  LinearProgress,
} from '@mui/material';
import { useTheme }     from '@mui/material/styles';
import api              from '../../services/api';
import { useAuth }      from '../../contexts/AuthContext';

import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import RefreshIcon        from '@mui/icons-material/Refresh';
import PaymentsIcon       from '@mui/icons-material/Payments';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import HourglassTopIcon   from '@mui/icons-material/HourglassTop';
import PendingIcon        from '@mui/icons-material/Pending';
import CancelIcon         from '@mui/icons-material/Cancel';

// ─── Types ────────────────────────────────────────────────────────────────────
type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'void' | 'pending_confirmation';

type LineItem = { description: string; quantity: number; unitPrice: number; amount: number };

type PartialPayment = {
  _id: string;
  amount: number;
  reference: string | null;
  paidAt: string;
  notes: string | null;
};

type Invoice = {
  _id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  currency: string;
  total: number;
  subtotal: number;
  amountPaid: number;
  amountRemaining: number;
  isPartiallyPaid: boolean;
  periodStart: string;
  periodEnd: string;
  issuedAt: string;
  dueAt: string;
  paidAt?: string;
  lineItems: LineItem[];
  partialPayments?: PartialPayment[];
  paymentSubmittedReference?: string;
  paymentSubmittedAt?: string;
  paymentRejectionReason?: string;
  paymentRejectedAt?: string;
  notes?: string;
};

type BankDetails = {
  bankName: string;
  accountNumber: string;
  accountName: string;
  sortCode?: string;
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  draft:                { label: 'Draft',    color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', icon: <ReceiptLongIcon sx={{ fontSize: 12 }} /> },
  sent:                 { label: 'Unpaid',   color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  icon: <WarningAmberIcon sx={{ fontSize: 12 }} /> },
  paid:                 { label: 'Paid',     color: '#34d399', bg: 'rgba(52,211,153,0.12)',  icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
  overdue:              { label: 'Overdue',  color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: <CancelIcon sx={{ fontSize: 12 }} /> },
  void:                 { label: 'Void',     color: '#64748b', bg: 'rgba(100,116,139,0.10)', icon: <CancelIcon sx={{ fontSize: 12 }} /> },
  pending_confirmation: { label: 'Pending',  color: '#38bdf8', bg: 'rgba(56,189,248,0.12)',  icon: <HourglassTopIcon sx={{ fontSize: 12 }} /> },
};

const fmt = (n: number, currency = 'NGN') => {
  try { return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(n); }
  catch { return `${currency} ${(n || 0).toFixed(2)}`; }
};

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

// ─── Submit Payment Dialog ────────────────────────────────────────────────────
interface SubmitPaymentDialogProps {
  open:        boolean;
  invoice:     Invoice | null;
  bankDetails: BankDetails | null;
  onClose:     () => void;
  onSubmitted: () => void;
}

const SubmitPaymentDialog: React.FC<SubmitPaymentDialogProps> = ({
  open, invoice, bankDetails, onClose, onSubmitted,
}) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [ref, setRef]         = React.useState('');
  const [notes, setNotes]     = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError]     = React.useState('');

  React.useEffect(() => { if (open) { setRef(''); setNotes(''); setError(''); } }, [open]);

  const border  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, fontSize: '0.875rem',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& fieldset':                   { borderColor: border },
      '&:hover fieldset':             { borderColor: '#34d399' },
      '&.Mui-focused fieldset':       { borderColor: '#34d399' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34d399' },
  };

  const handleSubmit = async () => {
    if (!ref.trim()) { setError('Payment reference is required'); return; }
    setLoading(true);
    try {
      await api.post(`/company-invoices/${invoice!._id}/submit-payment`, {
        paymentReference: ref.trim(),
        paymentNotes:     notes.trim() || undefined,
      });
      onSubmitted();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to submit payment reference');
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;
  const amountDue = invoice.amountRemaining ?? invoice.total;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
            <PaymentsIcon sx={{ color: '#34d399', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: isDark ? '#fff' : '#0f172a' }}>
              Submit Payment Reference
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: subtext }}>{invoice.invoiceNumber}</Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Amount due */}
        <Box sx={{ p: 1.5, borderRadius: 2, background: isDark ? 'rgba(56,189,248,0.08)' : 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.2)', mb: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.8rem', color: subtext }}>
              {invoice.isPartiallyPaid ? 'Remaining Balance' : 'Amount Due'}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: '#38bdf8' }}>
              {fmt(amountDue, invoice.currency)}
            </Typography>
          </Stack>
          {invoice.isPartiallyPaid && (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: subtext }}>Already paid</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#34d399', fontWeight: 700 }}>
                {fmt(invoice.amountPaid, invoice.currency)}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: subtext }}>Due Date</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: subtext }}>{fmtDate(invoice.dueAt)}</Typography>
          </Stack>

          {/* Partial payment progress */}
          {invoice.isPartiallyPaid && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={Math.round((invoice.amountPaid / invoice.total) * 100)}
                sx={{
                  height: 6, borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#34d399', borderRadius: 3 },
                }}
              />
              <Typography sx={{ fontSize: '0.65rem', color: subtext, mt: 0.5, textAlign: 'right' }}>
                {Math.round((invoice.amountPaid / invoice.total) * 100)}% paid
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bank details */}
        {bankDetails?.accountNumber && (
          <Box sx={{ p: 1.5, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: `1px solid ${border}`, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <AccountBalanceIcon sx={{ fontSize: 16, color: '#a78bfa' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Pay to this account
              </Typography>
            </Stack>
            {[
              { label: 'Bank',           value: bankDetails.bankName },
              { label: 'Account Name',   value: bankDetails.accountName },
              { label: 'Account Number', value: bankDetails.accountNumber },
              ...(bankDetails.sortCode ? [{ label: 'Sort Code', value: bankDetails.sortCode }] : []),
              { label: 'Reference',      value: invoice.invoiceNumber },
            ].map(item => (
              <Stack key={item.label} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
                <Typography sx={{ fontSize: '0.75rem', color: subtext }}>{item.label}</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: isDark ? '#fff' : '#0f172a' }}>{item.value}</Typography>
              </Stack>
            ))}
          </Box>
        )}

        <Stack spacing={2}>
          <TextField fullWidth label="Bank Transfer Reference *" value={ref}
            onChange={e => setRef(e.target.value)} size="small" sx={fieldSx}
            placeholder="e.g. TRF20240201001"
            helperText="Enter the reference number from your bank transfer receipt"
          />
          <TextField fullWidth label="Additional Notes (optional)" value={notes}
            onChange={e => setNotes(e.target.value)} size="small" sx={fieldSx} multiline rows={2}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
          {loading ? <CircularProgress size={18} /> : 'Submit Reference'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Invoice Detail Dialog ────────────────────────────────────────────────────
interface InvoiceDetailProps {
  open:            boolean;
  invoice:         Invoice | null;
  bankDetails:     BankDetails | null;
  onClose:         () => void;
  onSubmitPayment: () => void;
}

const InvoiceDetailDialog: React.FC<InvoiceDetailProps> = ({
  open, invoice, bankDetails, onClose, onSubmitPayment,
}) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border       = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext      = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#fff' : '#0f172a';

  if (!invoice) return null;
  const cfg    = STATUS_CONFIG[invoice.status];
  const canPay = ['sent', 'overdue'].includes(invoice.status) && !invoice.paymentSubmittedReference;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: 'rgba(251,191,36,0.12)', display: 'grid', placeItems: 'center' }}>
              <ReceiptLongIcon sx={{ color: '#fbbf24', fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: headingColor }}>{invoice.invoiceNumber}</Typography>
          </Stack>
          <Box sx={{ px: 1.5, py: 0.35, borderRadius: 2, background: cfg.bg, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{cfg.label}</Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          {/* Dates */}
          <Box>
            {[
              { label: 'Period', value: `${fmtDate(invoice.periodStart)} → ${fmtDate(invoice.periodEnd)}` },
              { label: 'Issued', value: fmtDate(invoice.issuedAt) },
              { label: 'Due',    value: fmtDate(invoice.dueAt) },
              ...(invoice.paidAt ? [{ label: 'Paid', value: fmtDate(invoice.paidAt) }] : []),
            ].map(item => (
              <Stack key={item.label} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: `1px solid ${border}` }}>
                <Typography sx={{ fontSize: '0.78rem', color: subtext, fontWeight: 700 }}>{item.label}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: headingColor, fontWeight: 700 }}>{item.value}</Typography>
              </Stack>
            ))}
          </Box>

          {/* Partial payment progress */}
          {invoice.isPartiallyPaid && invoice.status !== 'paid' && (
            <Box sx={{ p: 1.5, borderRadius: 2, background: isDark ? 'rgba(52,211,153,0.06)' : 'rgba(52,211,153,0.04)', border: '1px solid rgba(52,211,153,0.2)' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#34d399' }}>Partial payments received</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: headingColor }}>
                  {fmt(invoice.amountPaid, invoice.currency)} / {fmt(invoice.total, invoice.currency)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.round((invoice.amountPaid / invoice.total) * 100)}
                sx={{
                  height: 6, borderRadius: 3,
                  bgcolor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#34d399', borderRadius: 3 },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '0.7rem', color: subtext }}>Remaining</Typography>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#f87171' }}>
                  {fmt(invoice.amountRemaining, invoice.currency)}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Partial payment history */}
          {invoice.partialPayments && invoice.partialPayments.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                Payment History
              </Typography>
              {invoice.partialPayments.map((p) => (
                <Stack key={p._id} direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ py: 0.75, borderBottom: `1px solid ${border}` }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: headingColor }}>
                      {fmt(p.amount, invoice.currency)}
                    </Typography>
                    {p.reference && (
                      <Typography sx={{ fontSize: '0.68rem', color: subtext }}>Ref: {p.reference}</Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: subtext }}>{fmtDate(p.paidAt)}</Typography>
                </Stack>
              ))}
            </Box>
          )}

          {/* Line items */}
          {invoice.lineItems?.length > 0 && (
            <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${border}`, overflow: 'hidden' }}>
              {invoice.lineItems.map((li, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ px: 2, py: 1.25, borderBottom: i < invoice.lineItems.length - 1 ? `1px solid ${border}` : 'none' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: headingColor }}>{li.description}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: subtext }}>Qty: {li.quantity} × {fmt(li.unitPrice, invoice.currency)}</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>{fmt(li.amount, invoice.currency)}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.5, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>Total</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#34d399' }}>{fmt(invoice.total, invoice.currency)}</Typography>
              </Stack>
            </Paper>
          )}

          {/* Pending confirmation */}
          {invoice.status === 'pending_confirmation' && (
            <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HourglassTopIcon sx={{ color: '#38bdf8', fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#38bdf8' }}>
                  Payment reference submitted — awaiting confirmation
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: subtext, mt: 0.5 }}>
                Ref: <strong style={{ color: isDark ? '#fff' : '#0f172a' }}>{invoice.paymentSubmittedReference}</strong>
                {' · '}{fmtDate(invoice.paymentSubmittedAt)}
              </Typography>
            </Box>
          )}

          {/* Rejection notice */}
          {invoice.paymentRejectedAt && invoice.status === 'overdue' && (
            <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CancelIcon sx={{ color: '#f87171', fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#f87171' }}>
                  Previous payment reference was rejected
                </Typography>
              </Stack>
              {invoice.paymentRejectionReason && (
                <Typography sx={{ fontSize: '0.72rem', color: subtext, mt: 0.5 }}>
                  Reason: {invoice.paymentRejectionReason}
                </Typography>
              )}
            </Box>
          )}

          {invoice.notes && (
            <Typography sx={{ fontSize: '0.78rem', color: subtext, fontStyle: 'italic' }}>{invoice.notes}</Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>Close</Button>
        {canPay && (
          <Button variant="contained" onClick={() => { onClose(); onSubmitPayment(); }}
            startIcon={<PaymentsIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
            Submit Payment Reference
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const InvoicesPage: React.FC = () => {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { billingStatus, refreshBillingStatus } = useAuth();

  const [invoices, setInvoices]         = React.useState<Invoice[]>([]);
  const [page, setPage]                   = React.useState(1);
  const [totalPages, setTotalPages]       = React.useState(1);
  const [total, setTotal]                 = React.useState(0);
  const LIMIT = 10;
  const [bankDetails, setBankDetails]   = React.useState<BankDetails | null>(null);
  const [loading, setLoading]           = React.useState(true);
  const [error, setError]               = React.useState('');
  const [success, setSuccess]           = React.useState('');
  const [detailInvoice, setDetailInvoice]   = React.useState<Invoice | null>(null);
  const [paymentInvoice, setPaymentInvoice] = React.useState<Invoice | null>(null);

  const surface      = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border       = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext      = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';

  const fetchInvoices = async (p = page) => {
    setLoading(true); setError('');
    try {
      const [invRes, currentRes] = await Promise.all([
        api.get('/company-invoices', { params: { page: p, limit: LIMIT } }),
        api.get('/company-invoices/current'),
      ]);
      setInvoices(Array.isArray(invRes.data.data) ? invRes.data.data : []);
      setTotal(invRes.data.pagination?.total || 0);
      setTotalPages(invRes.data.pagination?.pages || 1);
      setBankDetails(currentRes.data.data?.bankDetails || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchInvoices(page); }, [page]);

  const currentInvoice = invoices.find(inv => {
    const now   = new Date();
    const start = new Date(inv.periodStart);
    return start.getFullYear() === now.getFullYear() && start.getMonth() === now.getMonth();
  });

  const handlePaymentSubmitted = () => {
    setSuccess('Payment reference submitted. Your account will be unlocked once confirmed.');
    fetchInvoices();
    refreshBillingStatus(); // update grace banner / lock screen
  };

  return (
    <Box>
      <SubmitPaymentDialog
        open={!!paymentInvoice}
        invoice={paymentInvoice}
        bankDetails={bankDetails}
        onClose={() => setPaymentInvoice(null)}
        onSubmitted={handlePaymentSubmitted}
      />
      <InvoiceDetailDialog
        open={!!detailInvoice}
        invoice={detailInvoice}
        bankDetails={bankDetails}
        onClose={() => setDetailInvoice(null)}
        onSubmitPayment={() => setPaymentInvoice(detailInvoice)}
      />

      {/* ── Header ── */}
      <PageHeader title="Invoices" description="View billing history, invoices, and payment status" icon="invoices" actions={<>
<Tooltip title="Refresh">
          <IconButton onClick={() => fetchInvoices(page)} size="small" sx={{ border: `1px solid ${border}`, borderRadius: 1.5, color: subtext }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
</>} />

      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      {/* ── Billing status warning banner (grace/locked) ── */}
      {billingStatus?.status === 'grace_period' && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.05)', mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberIcon sx={{ color: '#fbbf24' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', color: headingColor }}>
                Grace period active — {billingStatus.daysUntilLock != null ? `${billingStatus.daysUntilLock} day(s) until lockout` : 'account will be locked soon'}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: subtext }}>
                Pay your outstanding invoice before the grace period ends to maintain access.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* ── Current invoice action banner ── */}
      {!loading && currentInvoice && ['sent', 'overdue'].includes(currentInvoice.status) && (
        <Paper elevation={0} sx={{
          p: 2, borderRadius: 3, mb: 2,
          border: `1px solid ${currentInvoice.status === 'overdue' ? 'rgba(248,113,113,0.3)' : 'rgba(251,191,36,0.3)'}`,
          background: currentInvoice.status === 'overdue' ? 'rgba(248,113,113,0.06)' : 'rgba(251,191,36,0.06)',
        }}>
          <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WarningAmberIcon sx={{ color: currentInvoice.status === 'overdue' ? '#f87171' : '#fbbf24', fontSize: 22 }} />
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: headingColor }}>
                  {currentInvoice.status === 'overdue' ? 'Invoice Overdue' : 'Invoice Pending Payment'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: subtext }}>
                  {currentInvoice.invoiceNumber}
                  {' · '}
                  {fmt(currentInvoice.amountRemaining ?? currentInvoice.total, currentInvoice.currency)} remaining
                  {' · Due '}
                  {fmtDate(currentInvoice.dueAt)}
                  {currentInvoice.paymentSubmittedReference && (
                    <Chip label="Ref submitted — awaiting confirmation" size="small"
                      sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700, background: 'rgba(56,189,248,0.12)', color: '#38bdf8' }} />
                  )}
                </Typography>
              </Box>
            </Stack>
            {!currentInvoice.paymentSubmittedReference && (
              <Button variant="contained" startIcon={<PaymentsIcon />}
                onClick={() => setPaymentInvoice(currentInvoice)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' }, flexShrink: 0 }}>
                Submit Payment Reference
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* ── Invoices table ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                {['Invoice', 'Period', 'Issued', 'Due', 'Status', 'Amount', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, borderBottom: `1px solid ${border}`, py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j} sx={{ borderBottom: `1px solid ${border}` }}><Skeleton height={20} /></TableCell>)}</TableRow>
                ))
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ borderBottom: 'none' }}>
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <ReceiptLongIcon sx={{ fontSize: 36, color: subtext, mb: 1 }} />
                      <Typography sx={{ fontWeight: 800, color: headingColor }}>No invoices yet</Typography>
                      <Typography variant="body2" sx={{ color: subtext, mt: 0.5 }}>Invoices are generated automatically on the 1st of each month</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : invoices.map(inv => {
                const cfg = STATUS_CONFIG[inv.status];
                const amountDisplay = inv.isPartiallyPaid && inv.status !== 'paid'
                  ? `${fmt(inv.amountRemaining, inv.currency)} left`
                  : fmt(inv.total, inv.currency);

                return (
                  <TableRow key={inv._id} hover sx={{ '&:hover': { background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }, '& td': { borderBottom: `1px solid ${border}` } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.83rem', color: headingColor }}>{inv.invoiceNumber}</Typography>
                      {inv.isPartiallyPaid && inv.status !== 'paid' && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#34d399', fontWeight: 700 }}>Partial payment recorded</Typography>
                      )}
                      {inv.paymentSubmittedReference && inv.status === 'pending_confirmation' && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#38bdf8', fontWeight: 700 }}>Ref submitted</Typography>
                      )}
                      {inv.paymentRejectedAt && inv.status === 'overdue' && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#f87171', fontWeight: 700 }}>Ref rejected</Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.75rem', color: subtext }}>{fmtDate(inv.periodStart)} → {fmtDate(inv.periodEnd)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', color: subtext }}>{fmtDate(inv.issuedAt)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', color: subtext }}>{fmtDate(inv.dueAt)}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: 2, background: cfg.bg }}>
                        <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: cfg.color, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{cfg.label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 900, fontSize: '0.875rem', color: headingColor }}>{amountDisplay}</Typography>
                      {inv.isPartiallyPaid && inv.status !== 'paid' && (
                        <Typography sx={{ fontSize: '0.65rem', color: subtext }}>of {fmt(inv.total, inv.currency)}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setDetailInvoice(inv)} sx={{ color: subtext, '&:hover': { color: '#38bdf8' } }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {['sent', 'overdue'].includes(inv.status) && !inv.paymentSubmittedReference && (
                          <Tooltip title="Submit payment reference">
                            <IconButton size="small" onClick={() => setPaymentInvoice(inv)} sx={{ color: '#34d399', '&:hover': { background: 'rgba(52,211,153,0.1)' } }}>
                              <PaymentsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 1 }}>
          <Typography variant='body2' sx={{ color: subtext }}>
            Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total} invoices
          </Typography>
          <Stack direction='row' spacing={1}>
            <Button size='small' variant='outlined' disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              sx={{ minWidth: 32, textTransform: 'none', borderRadius: 1.5 }}>Prev</Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <Button key={p} size='small'
                variant={p === page ? 'contained' : 'outlined'}
                onClick={() => setPage(p)}
                sx={{ minWidth: 32, textTransform: 'none', borderRadius: 1.5 }}>{p}</Button>
            ))}
            <Button size='small' variant='outlined' disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              sx={{ minWidth: 32, textTransform: 'none', borderRadius: 1.5 }}>Next</Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default InvoicesPage;